import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import LoginPage from "./components/LoginPage";

import { APP_WORDS_SOURCE } from "./data/words";
import wordsZhuanSi from "./data/words_zhuansi.js";
import wordsAroundWorld from "./data/words_around_world.js";
import wordsLittlePrince from "./data/words_little_prince.js";
import wordsNumberDate from "./data/words_number_date.js";
import wordsFood from "./data/words_food.js";
import { PARIS_LANDMARKS } from "./data/landmarks";
import { HOME_SKINS, SHARD_CONFIG, LANDMARK_LIST } from "./constants/landmarkResources";
import LandmarkCarousel from "./components/home/LandmarkCarousel";

import COLORS from "./constants/colors";
import {
  DETAIL_CONTEXT_KEY,
  LANDMARK_UNLOCK_PREFIX,
} from "./constants/storageKeys";

// ✅ 词书配置（包含词库5）
const ALL_BOOKS = [
  {
    id: "base",
    name: "基础法语词库",
    words: APP_WORDS_SOURCE,
    cover: "/cover/基础法语词库.png"
  },
  {
    id: "zhuan-si",
    name: "法语专四词汇",
    words: wordsZhuanSi,
    cover: "/cover/专四.png"
  },
  {
    id: "around-world",
    name: "八十天环游地球",
    words: wordsAroundWorld,
    cover: "/cover/八十天环游地球.png"
  },
  {
    id: "little-prince",
    name: "小王子",
    words: wordsLittlePrince,
    cover: "/cover/小王子.png"
  },
  {
    id: "number-date",
    name: "数字与时间",
    words: wordsNumberDate,
    cover: "/cover/数字与时间.png"
  },
  {
    id: "food",
    name: "法语餐饮常用词",
    words: wordsFood,
    cover: "/cover/法语餐饮常用词.png"
  }
];

import {
  addLearnHistory,
  addReviewHistory,
  addSpellingHistory,
  createMemoryCapsule,
  ensureStarred,
  getDefaultState,
  loadState,
  markSpellingWrong,
  saveState,
  setWordProgress,
} from "./state/appState";

import { formatDateCN, formatWeekdayCN } from "./utils/date";
import { shuffle } from "./utils/helpers";
import { speakFrench } from "./utils/speech";
import { loadWeatherInfo } from "./utils/weather";
import { wordApi, learningApi } from "./utils/api";

import EntranceAnimation from "./components/common/EntranceAnimation";
import HomePage from "./components/home/HomePage";
import MyContentPage from "./components/content/MyContentPage";
import StatisticsPage from "./components/home/StatisticsPage";
import EbbinghausTable from "./components/home/EbbinghausTable";
import WordDetailPage from "./components/home/WordDetailPage";
import CompleteCard from "./components/common/CompleteCard";
import SpellingSummaryCard from "./components/spelling/SpellingSummaryCard";
import StarredWordsPage from "./components/content/StarredWordsPage";

import LearnPhaseOne from "./components/learn/LearnPhaseOne";
import LearnPhaseTwo from "./components/learn/LearnPhaseTwo";
import LearnPhaseThree from "./components/learn/LearnPhaseThree";

import ReviewPhaseOne from "./components/review/ReviewPhaseOne";
import ReviewPhaseTwoInput from "./components/review/ReviewPhaseTwoInput";

import SpellingPracticePage from "./components/spelling/SpellingPracticePage";
import SpellingPromptPage from "./components/spelling/SpellingPromptPage";

const buttonStyle = {
  transition: "transform 0.2s ease-in-out",
};

const hoverButtonStyle = {
  ":hover": {
    transform: "scale(1.05)",
    cursor: "pointer",
  },
};

function getWordById(id, words) {
  return words.find((w) => w.id === id) || null;
}

function uniquePush(arr, value) {
  return arr.includes(value) ? arr : [...arr, value];
}

function normalizeText(text) {
  return String(text || "")
    .trim()
    .replace(/\s+/g, "")
    .toLowerCase();
}

function buildStableOptions(wordId, words, correctTranslation) {
  const distractors = shuffle(
    words
      .filter((w) => w.id !== wordId)
      .map((w) => w.translation)
      .filter(Boolean)
  ).slice(0, 3);

  return shuffle([correctTranslation, ...distractors]).slice(0, 4);
}

const PrivateRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  const token = localStorage.getItem("token");
  if (!isLoggedIn || !token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const BASE_URL = process.env.REACT_APP_API_URL || "/api";
const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return fetch(`${BASE_URL}${url}`, {
    ...options,
    headers,
  });
};

export default function App() {
  const navigate = useNavigate();

  const [state, setState] = useState(getDefaultState());
  const mountedRef = useRef(false);

  // 🔥 核心新增：地标翻转索引状态
  const [currentLandmarkIndex, setCurrentLandmarkIndex] = useState(0);

  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem("isLoggedIn") === "true";
  });
  const [words, setWords] = useState(APP_WORDS_SOURCE);
  const [favorites, setFavorites] = useState([]);
  const [isLoadingWords, setIsLoadingWords] = useState(false);
  const [hasImportedWords, setHasImportedWords] = useState(() => {
    return localStorage.getItem("has_imported_words_to_backend") === "true";
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [studyStats, setStudyStats] = useState(null);

  const [showMapModal, setShowMapModal] = useState(false);

  // ✅ 新增：词书切换状态
  const [currentBook, setCurrentBook] = useState(ALL_BOOKS[0]); // 默认使用第一个词书

  // ✅ 新增：词书切换处理函数
  const handleSwitchBook = (book) => {
    setCurrentBook(book);
    showTempMessage(`已切换到：${book.name}`, 1500);
  };

  // ==============================================
  // 提示
  // ==============================================
  const showTempMessage = (text, ms = 2200) => {
    setState(prev => ({ ...prev, ui: { ...prev.ui, message: text } }));
    if (ms > 0) {
      setTimeout(() => {
        setState(prev => {
          if (prev.ui.message !== text) return prev;
          return { ...prev, ui: { ...prev.ui, message: "" } };
        });
      }, ms);
    }
  };

  // ==============================================
  // 🔥 核心：地标左右翻转方法（补全缺失功能）
  // ==============================================
  const onSwitchLandmark = (step) => {
    setCurrentLandmarkIndex(prev => {
      let newIndex = prev + step;
      // 循环切换：到头后回到起点/终点
      if (newIndex < 0) newIndex = LANDMARK_LIST.length - 1;
      if (newIndex >= LANDMARK_LIST.length) newIndex = 0;
      return newIndex;
    });
    showTempMessage(`切换至：${LANDMARK_LIST[currentLandmarkIndex + step]?.name || "地标"}`, 1500);
  };

  // ==============================================
  // 皮肤 + 地标壁纸 核心逻辑
  // ==============================================
  // 碎片掉落
  const dropRandomShards = (eventType) => {
    if (!SHARD_CONFIG.dropEvents.includes(eventType)) return;
    const drop = Math.floor(Math.random() * (SHARD_CONFIG.maxDrop - SHARD_CONFIG.minDrop + 1)) + SHARD_CONFIG.minDrop;
    
    setState(prev => {
      const newShards = prev.user.shards + drop;
      showTempMessage(`🎉 掉落碎片 +${drop}！当前碎片：${newShards}`, 2500);
      return {
        ...prev,
        user: { ...prev.user, shards: newShards }
      };
    });
  };

  // 解锁皮肤
  const unlockSkin = (skinId) => {
    const skin = HOME_SKINS.find(s => s.id === skinId);
    if (!skin) return;
    if (state.user.unlockedSkins.includes(skinId)) {
      showTempMessage("✅ 皮肤已解锁", 1500);
      return;
    }
    if (state.user.shards < skin.needShards) {
      showTempMessage("❌ 碎片不足", 1500);
      return;
    }

    setState(prev => ({
      ...prev,
      user: {
        ...prev.user,
        shards: prev.user.shards - skin.needShards,
        unlockedSkins: [...prev.user.unlockedSkins, skinId]
      }
    }));
    showTempMessage(`✅ 成功解锁【${skin.name}】`, 2000);
  };

  // 切换皮肤（支持地标壁纸）
  const changeSkin = (skinId) => {
    if (!state.user.unlockedSkins.includes(skinId) && !skinId.startsWith("landmark_")) {
      showTempMessage("❌ 请先解锁该皮肤", 1500);
      return;
    }
    setState(prev => ({
      ...prev,
      user: { ...prev.user, currentSkin: skinId }
    }));
    showTempMessage("✅ 壁纸切换成功", 1500);
  };

  // 解锁地标
  const unlockLandmark = (landmark) => {
    if (state.user.unlockedLandmarks.includes(landmark.id)) {
      showTempMessage("✅ 该地图已解锁", 1500);
      return;
    }
    if (state.user.shards < landmark.needShards) {
      showTempMessage("❌ 碎片不足", 1500);
      return;
    }

    setState(prev => ({
      ...prev,
      user: {
        ...prev.user,
        shards: prev.user.shards - landmark.needShards,
        unlockedLandmarks: [...prev.user.unlockedLandmarks, landmark.id]
      }
    }));
    showTempMessage(`✅ 成功解锁【${landmark.name}】地图 & 壁纸！`, 2000);
  };

  // 进入地标
  const enterLandmark = (landmark) => {
    setShowMapModal(false);
    showTempMessage(`正在进入【${landmark.name}】地图`);
  };

  // 🔥 核心：当前壁纸（支持普通皮肤 + 已解锁地标）
  const currentSkinBg = useMemo(() => {
    const currentSkinId = state.user.currentSkin;
    // 地标皮肤：格式 landmark_1
    if (currentSkinId?.startsWith("landmark_")) {
      const landmarkId = parseInt(currentSkinId.replace("landmark_", ""));
      const landmark = LANDMARK_LIST.find(l => l.id === landmarkId);
      return landmark?.bgUrl || "/paris-bg.jpg";
    }
    // 普通皮肤
    const skin = HOME_SKINS.find(s => s.id === currentSkinId);
    return skin?.bgUrl || "/paris-bg.jpg";
  }, [state.user.currentSkin]);

  // 🔥 合并皮肤列表：基础皮肤 + 已解锁地标（自动变成壁纸）
  const combinedSkinList = useMemo(() => {
    // 1. 基础官方皮肤
    const baseSkins = [...HOME_SKINS];
    // 2. 已解锁地标 → 自动加入皮肤列表
    const unlockedLandmarkSkins = state.user.unlockedLandmarks.map(id => {
      const landmark = LANDMARK_LIST.find(l => l.id === id);
      return {
        id: `landmark_${id}`,
        name: `${landmark.name}`,
        bgUrl: landmark.bgUrl,
        isDefault: false,
        needShards: 0,
        isLandmark: true
      };
    });
    return [...baseSkins, ...unlockedLandmarkSkins];
  }, [state.user.currentSkin, state.user.unlockedLandmarks]);

  // ==============================================
  // 原有业务逻辑（完全不动）
  // ==============================================
  const importWordsToBackend = async (forceImport = false) => {
    if (!forceImport && hasImportedWords) {
      console.log("✅ 单词已导入过后端，跳过重复导入");
      return;
    }

    if (!localStorage.getItem("isLoggedIn") || !localStorage.getItem("token")) {
      console.log("⚠️ 未登录，无法导入单词到后端");
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    showTempMessage("📥 正在导入单词到后端，请稍候...", 0);

    try {
      try {
        const checkData = await wordApi.getWordList(1, 1);
        if ((checkData && (checkData.total || (checkData.words && checkData.words.length))) && !forceImport) {
          console.log("✅ 后端已有单词数据，跳过导入");
          setHasImportedWords(true);
          localStorage.setItem("has_imported_words_to_backend", "true");
          setIsImporting(false);
          showTempMessage("✅ 后端已有单词数据", 1500);
          return;
        }
      } catch (err) {
        console.warn("检查后端单词失败，继续导入：", err);
      }

      const wordsToImport = APP_WORDS_SOURCE.map((word) => ({
        word: word.word,
        phonetic: word.phonetic || "",
        type: word.type || "",
        translation: word.translation,
        common_meanings: word.common_meanings || [],
        example: word.example || "",
        example_translation: word.example_translation || "",
        image: word.image || "",
        usages: word.usages || [],
        derivatives: word.derivatives || [],
        synonyms_antonyms: word.synonyms_antonyms || [],
      }));

      const batchSize = 50;
      let importedCount = 0;

      for (let i = 0; i < wordsToImport.length; i += batchSize) {
        const batch = wordsToImport.slice(i, i + batchSize);
        const importData = await wordApi.importWords(batch);
        if (importData) {
          importedCount += importData.imported || batch.length;
          const progress = Math.round((importedCount / wordsToImport.length) * 100);
          setImportProgress(progress);
        } else {
          throw new Error("导入失败");
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      setHasImportedWords(true);
      localStorage.setItem("has_imported_words_to_backend", "true");
      setIsImporting(false);
      showTempMessage(`🎉 成功导入 ${importedCount} 个单词！`, 2500);
      await loadWordsFromBackend();
    } catch (err) {
      console.error("❌ 单词导入失败：", err);
      setIsImporting(false);
      showTempMessage("❌ 单词导入失败", 2000);
    }
  };

  useEffect(() => {
    const initApp = async () => {
      try {
        const res = await fetch(`${BASE_URL}/`);
        if (res.ok) console.log("✅ 后端连通成功");
      } catch (err) {
        console.log("❌ 后端未启动");
      }

      if (localStorage.getItem("isLoggedIn") === "true") {
        await loadWordsFromBackend();
        await loadFavoritesFromBackend();
        await loadCheckInAndStats();
      }
    };
    initApp();
  }, [isLoggedIn]);

  const loadWordsFromBackend = async () => {
    try {
      const data = await wordApi.getWordList(1, 1000);
      if (data?.words?.length) setWords(data.words);
    } catch (err) {
      console.error("拉取单词失败", err);
    }
  };

  const loadFavoritesFromBackend = async () => {
    try {
      const data = await wordApi.getFavorites();
      if (Array.isArray(data)) setFavorites(data.map(item => item.word.id));
    } catch (err) {
      console.error("拉取收藏失败", err);
    }
  };

  const loadCheckInAndStats = async () => {
    try {
      const checkData = await learningApi.getCheckInStatus();
      if (checkData) setCheckInStatus(checkData);
    } catch (err) {
      console.error("加载签到状态失败", err);
    }

    try {
      const statsData = await learningApi.getLearningStats();
      if (statsData) setStudyStats(statsData);
    } catch (err) {
      console.error("加载学习统计失败", err);
    }
    
    // ✨ 新增: 加载碎片统计
    try {
      const fragmentStats = await learningApi.getFragmentStats();
      if (fragmentStats && fragmentStats.total_fragments !== undefined) {
        setState(prev => ({
          ...prev,
          user: {
            ...prev.user,
            shards: fragmentStats.total_fragments
          }
        }));
      }
    } catch (err) {
      console.error("加载碎片统计失败", err);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setWords(APP_WORDS_SOURCE);
    setFavorites([]);
    setCheckInStatus(null);
    setStudyStats(null);
    navigate("/login");
  };

  const handleLoginSuccess = async () => {
    setIsLoggedIn(true);
    await importWordsToBackend(false);
    await loadWordsFromBackend();
    await loadFavoritesFromBackend();
    await loadCheckInAndStats();
    navigate("/");
  };

  useEffect(() => {
    const loaded = loadState();
    loaded.app.currentDateText = formatDateCN(new Date());
    loaded.app.currentWeekdayText = formatWeekdayCN(new Date());
    loaded.app.weather = { ...loaded.app.weather, loading: true };
    if (loaded.app.hasShownEntrance === undefined) loaded.app.hasShownEntrance = false;
    if (!loaded.user.shards) loaded.user.shards = 0;
    if (!loaded.user.unlockedSkins) loaded.user.unlockedSkins = ["default"];
    if (!loaded.user.currentSkin) loaded.user.currentSkin = "default";
    if (!loaded.user.unlockedLandmarks) loaded.user.unlockedLandmarks = [1];
    setState(loaded);
    mountedRef.current = true;
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    saveState(state);
  }, [state]);

  useEffect(() => {
    let alive = true;
    async function initWeather() {
      try {
        const weather = await loadWeatherInfo();
        if (!alive) return;
        setState(prev => ({
          ...prev,
          app: {
            ...prev.app,
            currentDateText: formatDateCN(new Date()),
            currentWeekdayText: formatWeekdayCN(new Date()),
            weather: { ...weather, loading: false },
          },
        }));
      } catch {
        setState(prev => ({
          ...prev,
          app: {
            ...prev.app,
            currentDateText: formatDateCN(new Date()),
            currentWeekdayText: formatWeekdayCN(new Date()),
            weather: { loading: false, text: "巴黎天气", icon: "⛅", temperature: "--", source: "fallback" },
          },
        }));
      }
    }
    initWeather();
    return () => { alive = false };
  }, []);

  const goHome = () => {
    setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "home", message: "" } }));
  };

  const goToStarredWords = () => {
    setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "starredWords", message: "" } }));
  };

  const handleUnstarWord = (wordId) => {
    setFavorites(prev => prev.filter(id => id !== wordId));
    showTempMessage("❌ 已取消星标");
  };

  const handleGoHomeFromFlow = () => {
    showTempMessage("已返回主页", 1200);
    setTimeout(goHome, 300);
  };

  // 🔥 修正：当前地标 = 翻转索引对应的地标
  const currentLandmark = useMemo(() => {
    return LANDMARK_LIST[currentLandmarkIndex] || LANDMARK_LIST[0];
  }, [currentLandmarkIndex]);

  useEffect(() => {
    const unlocked = PARIS_LANDMARKS.filter(x => state.user.energy >= x.need);
    const latest = unlocked[unlocked.length - 1];
    if (!latest) return;
    const key = `${LANDMARK_UNLOCK_PREFIX}${latest.id}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      if (latest.id !== 1) {
        setState(prev => ({ ...prev, ui: { ...prev.ui, showLandmarkUnlock: latest } }));
      }
    }
  }, [state.user.energy]);

  const starredWords = useMemo(
    () => words.filter(w => favorites.includes(w.id)),
    [words, favorites]
  );

  const learnedWords = useMemo(
    () => words.filter(w => state.user.learnedWordIds.includes(w.id)),
    [words, state.user.learnedWordIds]
  );

  const closeLandmarkUnlock = () => {
    setState(prev => ({ ...prev, ui: { ...prev.ui, showLandmarkUnlock: null } }));
  };

  const handleCheckIn = async () => {
    try {
      await learningApi.checkIn();
      await loadCheckInAndStats();
      setState(prev => ({
        ...prev,
        user: { ...prev.user, energy: prev.user.energy + 2 }
      }));
      dropRandomShards("check_in");
      showTempMessage("🎉 签到成功 +2 能量");
    } catch (err) {
      showTempMessage("✅ 今日已签到！");
      console.error("签到失败", err);
    }
  };

  const getCurrentWordIdFromUI = () => {
    const screen = state.ui.screen;
    if (screen.startsWith("learn")) return state.learnSession?.currentWordId || null;
    if (screen.startsWith("review")) return state.reviewSession?.currentWordId || null;
    if (screen === "detail") return state.ui.detailWordId || null;
    if (screen === "spelling") return state.spellingSession?.currentWordId || null;
    return null;
  };

  const handleStarCurrentWord = async () => {
    const wordId = getCurrentWordIdFromUI();
    if (!wordId) return;

    try {
      if (favorites.includes(wordId)) {
        const res = await wordApi.removeFavorite(wordId);
        if (res && res.ok !== false) {
          setFavorites(prev => prev.filter(id => id !== wordId));
          showTempMessage("❌ 已取消收藏");
        }
      } else {
        const res = await wordApi.addFavorite(wordId);
        if (res && res.ok !== false) {
          setFavorites(prev => [...prev, wordId]);
          showTempMessage("⭐ 收藏成功");
        }
      }
    } catch (err) {
      console.error("收藏失败：", err);
      showTempMessage("⚠️ 收藏失败，请检查后端");
    }
  };

  const openWordDetail = (wordId, context = null) => {
    setState(prev => ({
      ...prev,
      ui: {
        ...prev.ui,
        screen: "detail",
        detailWordId: wordId,
        detailTab: "usage",
        meaningExampleIndex: null,
        tempNote: prev.user.notes[wordId] || "",
      },
    }));
    context ? sessionStorage.setItem(DETAIL_CONTEXT_KEY, JSON.stringify(context))
      : sessionStorage.removeItem(DETAIL_CONTEXT_KEY);
  };

  const handleDailyPlanChange = (value) => {
    const next = Math.max(1, Math.min(100, Number(value) || 10));
    setState(prev => ({ ...prev, user: { ...prev.user, dailyPlanCount: next } }));
    showTempMessage(`📘 每日计划：${next} 词`, 1200);
  };

  const startLearn = async () => {
    if (words.length === 0) {
      showTempMessage("📘 词库为空");
      return;
    }
    const plan = state.user.dailyPlanCount || 10;
    let ids = [];
    // 如果已登录，优先使用后端的 next-word 接口获取新词（后端会创建 progress 记录）
    if (localStorage.getItem("isLoggedIn") === "true") {
      try {
        for (let i = 0; i < plan; i++) {
          try {
            const next = await learningApi.getNextStudyWord();
            if (next && next.word_id) ids.push(next.word_id);
            else break;
          } catch (err) {
            // 如果后端返回 404 或其他错误，停止拉取
            break;
          }
        }
      } catch (err) {
        console.warn("从后端拉取新词失败，回退到本地列表", err);
      }
    }

    // 如果没有从后端获得足够的新词，回退到本地筛选
    if (ids.length === 0) {
      ids = words
        .filter((w) => !state.user.learnedWordIds.includes(w.id))
        .slice(0, plan)
        .map((w) => w.id);
    }
    if (ids.length === 0) {
      showTempMessage("📘 暂无新词");
      return;
    }
    
    // 过滤掉本地不存在的单词ID，确保所有id都能找到对应的单词
    const validIds = ids.filter(id => getWordById(id, words) !== null);
    if (validIds.length === 0) {
      showTempMessage("📘 本地词库不包含这些单词");
      return;
    }
    
    const phase1Map = {}, phase2Map = {}, phase3Map = {};
    validIds.forEach(id => {
      const w = getWordById(id, words);
      if (!w || !w.translation) {
        console.warn(`单词ID ${id} 数据不完整，跳过`);
        return;
      }
      phase1Map[id] = { options: buildStableOptions(id, words, w.translation), triedOptions: [] };
      phase2Map[id] = { showHint: false };
      phase3Map[id] = { inputValue: "", checkResult: "idle" };
    });
    setState(prev => ({
      ...prev,
      learnSession: {
        stage: 1,
        words: validIds,
        stage1Index: 0,
        stage2Index: 0,
        stage3Index: 0,
        currentWordId: validIds[0],
        phase1Map, phase2Map, phase3Map, completed: false
      },
      ui: { ...prev.ui, screen: "learn1", message: "" },
    }));
  };

  const startReview = () => {
    (async () => {
      showTempMessage("✨ 正在加载复习任务...");
      try {
        // 优先从后端获取复习进度
        const progressResp = await learningApi.getProgress();
        let ids = [];
        if (progressResp && Array.isArray(progressResp.words)) {
          ids = progressResp.words.map(w => w.word_id || w.id).filter(Boolean);
        } else if (progressResp && Array.isArray(progressResp.progress)) {
          ids = progressResp.progress.map(p => p.word_id).filter(Boolean);
        }

        // 回退：如果后端没有返回具体单词，则从本地 state.user.wordProgress 选取到期项
        if (!ids || ids.length === 0) {
          const today = new Date().toISOString().slice(0, 10);
          ids = Object.keys(state.user.wordProgress)
            .filter(wid => {
              const p = state.user.wordProgress[wid];
              if (!p) return false;
              if (!p.reviewDue) return false;
              return p.reviewDue <= today;
            })
            .map(x => Number(x));
        }

        if (!ids || ids.length === 0) {
          showTempMessage("当前没有需要复习的单词，已跳转至学习统计页面", 1800);
          setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "statistics" } }));
          return;
        }

        // 初始化 reviewSession
        const phase1Map = {};
        ids.forEach(id => { phase1Map[id] = { tried: false }; });
        setState(prev => ({
          ...prev,
          reviewSession: {
              words: ids,
              stage1Index: 0,
              stage2Index: 0,
              currentWordId: ids[0],
              phase1Map,
              attemptMap: {},
              failedWords: [],
            },
          ui: { ...prev.ui, screen: "review1" }
        }));
      } catch (err) {
        console.warn("加载复习任务失败，回退到本地逻辑：", err);
        showTempMessage("加载复习任务失败，使用本地缓存", 1600);
        // 回退逻辑：选取 learnedWordIds 中部分作为复习
        const ids = state.user.learnedWordIds.slice(0, 20);
        if (ids.length === 0) { showTempMessage("暂无复习内容"); return; }
        const phase1Map = {};
        ids.forEach(id => { phase1Map[id] = { tried: false }; });
        setState(prev => ({ ...prev, reviewSession: { words: ids, stage1Index: 0, stage2Index: 0, currentWordId: ids[0], phase1Map, attemptMap: {}, failedWords: [] }, ui: { ...prev.ui, screen: "review1" } }));
      }
    })();
  };

  // 用户在复习阶段 1 点击「认识/模糊/不认识」的处理器
  const handleReviewKnown = (status) => {
    if (!currentReviewWord || !state.reviewSession) return;
    const id = currentReviewWord.id;
    (async () => {
      try {
        await learningApi.recordStudyResult(id, status === 'known' ? "correct" : "wrong", 20);
      } catch (err) {
        console.warn("记录复习结果到后端失败：", err);
      }
    })();

    // 更新本地状态并前进；处理 failedWords 和 attemptMap
    setState(prev => {
      let s = { ...prev.reviewSession };
      const next = s.stage1Index + 1;
      let nextState = { ...prev };

      if (status === 'known') {
        nextState.user = addReviewHistory(nextState, 1).user ? addReviewHistory(nextState,1).user : nextState.user;
      } else {
        // 标记为需要重学的单词
        const failed = Array.from(new Set([...(s.failedWords || []), id]));
        s.failedWords = failed;

        // unknown 增加尝试计数，连续 3 次 unknown 自动星标
        if (status === 'unknown') {
          const attempt = (s.attemptMap?.[id] || 0) + 1;
          s.attemptMap = { ...(s.attemptMap || {}), [id]: attempt };
          if (attempt >= 3) {
            nextState = ensureStarred(nextState, id);
          }
        }
      }

      if (next >= s.words.length) {
        s.currentWordId = s.words[0]; s.stage2Index = 0;
        nextState.reviewSession = s;
        nextState.ui = { ...prev.ui, screen: "review2" };
        return nextState;
      }
      s.stage1Index = next; s.currentWordId = s.words[next];
      nextState.reviewSession = s;
      nextState.ui = { ...prev.ui, screen: "review1" };
      return nextState;
    });
  };

  const currentLearnWord = state.learnSession?.currentWordId
    ? getWordById(state.learnSession.currentWordId, words)
    : null;

  const currentReviewWord = state.reviewSession?.currentWordId
    ? getWordById(state.reviewSession.currentWordId, words)
    : null;

  const currentSpellingWord = state.spellingSession?.currentWordId
    ? getWordById(state.spellingSession.currentWordId, words)
    : null;

  const gotoLearnStage2 = () => setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "learn2" } }));
  const gotoLearnStage3 = () => setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "learn3" } }));
  const gotoReviewStage2 = () => setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "review2" } }));

  const nextLearnFromDetail = (stage) => {
    if (!state.learnSession) return;
    if (stage === 1) {
      setState(prev => {
        const s = { ...prev.learnSession };
        const next = s.stage1Index + 1;
        if (next >= s.words.length) {
          s.currentWordId = s.words[0]; s.stage2Index = 0;
          return { ...prev, learnSession: s, ui: { ...prev.ui, screen: "learn2" } };
        }
        s.stage1Index = next; s.currentWordId = s.words[next];
        return { ...prev, learnSession: s, ui: { ...prev.ui, screen: "learn1" } };
      });
    }
    if (stage === 2) {
      setState(prev => {
        const s = { ...prev.learnSession };
        const next = s.stage2Index + 1;
        if (next >= s.words.length) {
          s.currentWordId = s.words[0]; s.stage3Index = 0;
          return { ...prev, learnSession: s, ui: { ...prev.ui, screen: "learn3" } };
        }
        s.stage2Index = next; s.currentWordId = s.words[next];
        return { ...prev, learnSession: s, ui: { ...prev.ui, screen: "learn2" } };
      });
    }
  };

  const nextReviewFromDetail = (stage) => {
    if (!state.reviewSession) return;
    if (stage === 1) {
      setState(prev => {
        const s = { ...prev.reviewSession };
        const next = s.stage1Index + 1;
        if (next >= s.words.length) {
          s.currentWordId = s.words[0]; s.stage2Index = 0;
          return { ...prev, reviewSession: s, ui: { ...prev.ui, screen: "review2" } };
        }
        s.stage1Index = next; s.currentWordId = s.words[next];
        return { ...prev, reviewSession: s, ui: { ...prev.ui, screen: "review1" } };
      });
    }
  };

  const jumpFromDetailNext = () => {
    const raw = sessionStorage.getItem(DETAIL_CONTEXT_KEY);
    if (!raw) { goHome(); return; }
    const ctx = JSON.parse(raw);
    if (ctx.type === "learn") { nextLearnFromDetail(ctx.stage); return; }
    if (ctx.type === "review") { nextReviewFromDetail(ctx.stage); return; }
    goHome();
  };

  const handleSaveNote = () => {
    const wordId = state.ui.detailWordId;
    if (!wordId) return;
    setState(prev => ({
      ...prev,
      user: { ...prev.user, notes: { ...prev.user.notes, [wordId]: prev.ui.tempNote } },
    }));
    showTempMessage("📝 笔记已保存");
  };

  const handleMarkDetailWrong = () => {
    const wordId = state.ui.detailWordId;
    if (!wordId) return;
    showTempMessage("❌ 记忆错误已记录");
    setTimeout(jumpFromDetailNext, 200);
  };

  const handleLearnChoice = (opt) => {
    if (!currentLearnWord || !state.learnSession) return;
    const id = currentLearnWord.id;
    const correct = opt === currentLearnWord.translation;
    setState(prev => {
      const s = { ...prev.learnSession };
      const map = { ...s.phase1Map };
      map[id] = {
        ...map[id],
        triedOptions: map[id].triedOptions.includes(opt)
          ? map[id].triedOptions
          : [...map[id].triedOptions, opt]
      };
      let next = { ...prev, learnSession: { ...s, phase1Map: map } };
      if (correct) {
        next.user.learnedWordIds = uniquePush(next.user.learnedWordIds, id);
      }
      return next;
    });
    if (correct) {
      speakFrench(currentLearnWord.word);
      showTempMessage("✅ 正确", 900);
      (async () => {
        try {
          await learningApi.recordStudyResult(id, "correct", 30);
        } catch (err) {
          console.warn("记录学习结果到后端失败：", err);
        }
        setTimeout(() => openWordDetail(id, { type: "learn", stage: 1 }), 350);
      })();
    } else {
      (async () => {
        try {
          await learningApi.recordStudyResult(id, "wrong", 10);
        } catch (err) {
          console.warn("记录学习结果到后端失败：", err);
        }
      })();
      showTempMessage("❌ 错误");
    }
  };

  const handleLearnPhase2Know = () => {
    if (!currentLearnWord) return;
    const id = currentLearnWord.id;
    setState(prev => {
      const newState = {
        ...prev,
        user: {
          ...prev.user,
          learnedWordIds: uniquePush(prev.user.learnedWordIds, id),
          energy: prev.user.energy + 1
        }
      };
      return newState;
    });
    addLearnHistory(state, 1);
    openWordDetail(id, { type: "learn", stage: 2 });
    (async () => {
      try {
        await learningApi.recordStudyResult(id, "correct", 20);
      } catch (err) {
        console.warn("记录学习结果到后端失败：", err);
      }
    })();
  };

  const handleLearnPhase2DontKnow = () => {
    if (!currentLearnWord) return;
    openWordDetail(currentLearnWord.id, { type: "learn", stage: 2 });
  };

  const handleLearnPhase3Check = () => {
    if (!currentLearnWord || !state.learnSession) return;
    const id = currentLearnWord.id;
    const input = state.learnSession.phase3Map[id]?.inputValue || "";
    const candidates = [
      currentLearnWord.translation,
      ...(currentLearnWord.common_meanings || [])
    ].map(normalizeText);
    const ok = candidates.includes(normalizeText(input));
    setState(prev => {
      const s = { ...prev.learnSession };
      s.phase3Map = {
        ...s.phase3Map,
        [id]: { ...s.phase3Map[id], checkResult: ok ? "correct" : "wrong" }
      };
      let next = { ...prev, learnSession: s };
      if (ok) {
        next.user.learnedWordIds = uniquePush(next.user.learnedWordIds, id);
      }
      return next;
    });
    if (ok) {
      (async () => {
        try {
          await learningApi.recordStudyResult(id, "correct", 25);
        } catch (err) {
          console.warn("记录学习结果到后端失败：", err);
        }
      })();
    } else {
      (async () => {
        try {
          await learningApi.recordStudyResult(id, "wrong", 10);
        } catch (err) {
          console.warn("记录学习结果到后端失败：", err);
        }
      })();
    }
  };

  const handleReviewStage1Choice = () => {
    if (!currentReviewWord) return;
    setTimeout(() => openWordDetail(currentReviewWord.id, { type: "review", stage: 1 }), 1000);
  };

  

  const handlePrevInFlow = () => {
    const screen = state.ui.screen;
    setState(prev => {
      if (screen === "learn1" && prev.learnSession) {
        const idx = Math.max(0, prev.learnSession.stage1Index - 1);
        return {
          ...prev,
          learnSession: { ...prev.learnSession, stage1Index: idx, currentWordId: prev.learnSession.words[idx] }
        };
      }
      if (screen === "learn2" && prev.learnSession) {
        const idx = Math.max(0, prev.learnSession.stage2Index - 1);
        return {
          ...prev,
          learnSession: { ...prev.learnSession, stage2Index: idx, currentWordId: prev.learnSession.words[idx] }
        };
      }
      if (screen === "learn3" && prev.learnSession) {
        const idx = Math.max(0, prev.learnSession.stage3Index - 1);
        return {
          ...prev,
          learnSession: { ...prev.learnSession, stage3Index: idx, currentWordId: prev.learnSession.words[idx] }
        };
      }
      if (screen === "review1" && prev.reviewSession) {
        const idx = Math.max(0, prev.reviewSession.stage1Index - 1);
        return {
          ...prev,
          reviewSession: { ...prev.reviewSession, stage1Index: idx, currentWordId: prev.reviewSession.words[idx] }
        };
      }
      if (screen === "review2" && prev.reviewSession) {
        const idx = Math.max(0, prev.reviewSession.stage2Index - 1);
        return {
          ...prev,
          reviewSession: { ...prev.reviewSession, stage2Index: idx, currentWordId: prev.reviewSession.words[idx] }
        };
      }
      if (screen === "spelling" && prev.spellingSession) {
        const idx = Math.max(0, prev.spellingSession.index - 1);
        return {
          ...prev,
          spellingSession: { ...prev.spellingSession, index: idx, currentWordId: prev.spellingSession.words[idx] }
        };
      }
      return prev;
    });
  };

  const handleSkipInFlow = () => {
    const screen = state.ui.screen;
    if (screen === "detail") { jumpFromDetailNext(); return; }
    if (screen === "learn1" && state.learnSession) {
      const next = state.learnSession.stage1Index + 1;
      if (next >= state.learnSession.words.length) gotoLearnStage2();
      else setState(prev => ({
        ...prev,
        learnSession: { ...prev.learnSession, stage1Index: next, currentWordId: prev.learnSession.words[next] }
      }));
      return;
    }
    if (screen === "learn2" && state.learnSession) {
      const next = state.learnSession.stage2Index + 1;
      if (next >= state.learnSession.words.length) gotoLearnStage3();
      else setState(prev => ({
        ...prev,
        learnSession: { ...prev.learnSession, stage2Index: next, currentWordId: prev.learnSession.words[next] }
      }));
      return;
    }
    if (screen === "learn3" && state.learnSession) {
      const next = state.learnSession.stage3Index + 1;
      if (next >= state.learnSession.words.length) {
        setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "learnComplete" } }));
      } else {
        setState(prev => ({
          ...prev,
          learnSession: { ...prev.learnSession, stage3Index: next, currentWordId: prev.learnSession.words[next] }
        }));
      }
      return;
    }
    if (screen === "review1" && state.reviewSession) {
      const next = state.reviewSession.stage1Index + 1;
      if (next >= state.reviewSession.words.length) gotoReviewStage2();
      else setState(prev => ({
        ...prev,
        reviewSession: { ...prev.reviewSession, stage1Index: next, currentWordId: prev.reviewSession.words[next] }
      }));
      return;
    }
    if (screen === "review2" && state.reviewSession) {
      const next = state.reviewSession.stage2Index + 1;
      if (next >= state.reviewSession.words.length) {
        setState(prev => {
          const failed = prev.reviewSession?.failedWords || [];
          if (failed && failed.length > 0) {
            // 将未掌握词转回学习流程
            const ids = failed.slice(0, prev.user.dailyPlanCount || 10);
            const phase1Map = {}; const phase2Map = {}; const phase3Map = {};
            ids.forEach(id => { phase1Map[id] = {}; phase2Map[id] = {}; phase3Map[id] = {}; });
            return {
              ...prev,
              learnSession: {
                stage: 1,
                words: ids,
                stage1Index: 0,
                stage2Index: 0,
                stage3Index: 0,
                currentWordId: ids[0],
                phase1Map, phase2Map, phase3Map, completed: false
              },
              ui: { ...prev.ui, screen: "learn1" }
            };
          }
          return { ...prev, ui: { ...prev.ui, screen: "reviewComplete" } };
        });
      } else {
        setState(prev => ({
          ...prev,
          reviewSession: { ...prev.reviewSession, stage2Index: next, currentWordId: prev.reviewSession.words[next] }
        }));
      }
      return;
    }
    if (screen === "spelling") advanceSpellingWord();
  };

  const startSpellingFromWords = (wordIds, source = "learn") => {
    const ids = wordIds.slice(0, state.user.dailyPlanCount || 10);
    if (ids.length === 0) { goHome(); return; }
    const results = {};
    ids.forEach(id => {
      results[id] = { wordId: id, wrongCount: 0, starred: favorites.includes(id) };
    });
    setState(prev => ({
      ...prev,
      spellingSession: {
        source,
        words: ids,
        index: 0,
        currentWordId: ids[0],
        inputValue: "",
        checkState: "idle",
        wrongHint: "",
        attemptMap: {},
        results,
        showSpellingHint: false
      },
      ui: { ...prev.ui, screen: "spelling" },
    }));
  };

  const advanceSpellingWord = () => {
    setState(prev => {
      if (!prev.spellingSession) return prev;
      const s = { ...prev.spellingSession };
      const next = s.index + 1;
      if (next >= s.words.length) {
        return { ...prev, ui: { ...prev.ui, screen: "spellingComplete" } };
      }
      return {
        ...prev,
        spellingSession: {
          ...s,
          index: next,
          currentWordId: s.words[next],
          inputValue: "",
          checkState: "idle",
          wrongHint: "",
          showSpellingHint: false
        }
      };
    });
  };

  const handleCheckSpelling = () => {
    if (!state.spellingSession || !currentSpellingWord) return;
    const typed = normalizeText(state.spellingSession.inputValue);
    const expected = normalizeText(currentSpellingWord.word);
    if (typed === expected) {
      setState(prev => {
        let next = {
          ...prev,
          spellingSession: { ...prev.spellingSession, checkState: "correct", wrongHint: "" }
        };
        next = addSpellingHistory(next, 1);
        return next;
      });
      setTimeout(advanceSpellingWord, 650);
      return;
    }
    setState(prev => {
      const s = { ...prev.spellingSession };
      const id = s.currentWordId;
      const attempt = (s.attemptMap[id] || 0) + 1;
      s.attemptMap = { ...s.attemptMap, [id]: attempt };
      s.results = {
        ...s.results,
        [id]: {
          ...s.results[id],
          wrongCount: (s.results[id].wrongCount || 0) + 1,
          starred: attempt >= 3 || favorites.includes(id)
        }
      };
      let next = { ...prev, spellingSession: { ...s, checkState: "wrong", wrongHint: "拼写有误" } };
      next = markSpellingWrong(next, id);
      if (attempt >= 3) next = ensureStarred(next, id);
      return next;
    });
  };

  // 学习进度：分母使用用户配置的每日学习计划数量，便于用户直观看到与计划的对比
  const planCount = state.user?.dailyPlanCount || 10;
  const currentLearnProgressText = state.learnSession
    ? state.ui.screen === "learn1" ? `${state.learnSession.stage1Index + 1}/${planCount}`
      : state.ui.screen === "learn2" ? `${state.learnSession.stage2Index + 1}/${planCount}`
        : `${state.learnSession.stage3Index + 1}/${planCount}`
    : `0/${planCount}`;

  // 复习进度同样使用用户每日计划作为分母，以便与学习计划一致
  const currentReviewProgressText = state.reviewSession
    ? state.ui.screen === "review1" ? `${state.reviewSession.stage1Index + 1}/${planCount}`
      : `${state.reviewSession.stage2Index + 1}/${planCount}`
    : `0/${planCount}`;

  // 🔥 透传：地标翻转方法 + 所有参数
  const renderHome = () => (
    <HomePage
      state={state}
      currentLandmark={currentLandmark}
      landmarks={PARIS_LANDMARKS}
      // 🔥 关键：传递地标翻转方法
      onSwitchLandmark={onSwitchLandmark}
      message={state.ui.message}
      onShowMessage={showTempMessage}
      onCheckIn={handleCheckIn}
      onStartLearn={startLearn}
      onStartReview={startReview}
      onGoMyContent={() => setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "myContent" } }))}
      onGoStatistics={() => setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "statistics" } }))}
      onGoMap={() => setShowMapModal(true)}
      onCloseLandmarkUnlock={closeLandmarkUnlock}
      onGoWordDetail={openWordDetail}
      onLogout={handleLogout}
      isImporting={isImporting}
      importProgress={importProgress}
      hasImportedWords={hasImportedWords}
      onImportWords={() => importWordsToBackend(true)}
      studyStats={studyStats}
      checkInStatus={checkInStatus}
      currentSkinBg={currentSkinBg}
      shards={state.user.shards}
      unlockedSkins={state.user.unlockedSkins}
      currentSkin={state.user.currentSkin}
      onChangeSkin={changeSkin}
      onUnlockSkin={unlockSkin}
      skinList={combinedSkinList}
      onUpdateStudyPlan={(v) => setState(prev => ({ ...prev, user: { ...prev.user, dailyPlanCount: v } }))}
      // ✅ 新增：词书切换功能
      books={ALL_BOOKS}
      currentBook={currentBook}
      onSwitchBook={handleSwitchBook}
    />
  );

  const renderDetail = () => {
    const word = getWordById(state.ui.detailWordId, words);
    return (
      <WordDetailPage
        word={word}
        detailTab={state.ui.detailTab}
        tempNote={state.ui.tempNote}
        meaningExampleIndex={state.ui.meaningExampleIndex}
        onSetDetailTab={tab => setState(prev => ({ ...prev, ui: { ...prev.ui, detailTab: tab } }))}
        onSetTempNote={val => setState(prev => ({ ...prev, ui: { ...prev.ui, tempNote: val } }))}
        onSetMeaningExampleIndex={val => setState(prev => ({ ...prev, ui: { ...prev.ui, meaningExampleIndex: val } }))}
        onSaveNote={handleSaveNote}
        onNext={jumpFromDetailNext}
        onMarkWrong={handleMarkDetailWrong}
        onPrev={handlePrevInFlow}
        onStar={handleStarCurrentWord}
        onSkip={handleSkipInFlow}
        onHome={handleGoHomeFromFlow}
        disablePrev={false}
      />
    );
  };

  const renderLearn1 = () => {
    if (!currentLearnWord || !state.learnSession) return renderHome();
    const record = state.learnSession.phase1Map[currentLearnWord.id];
    return (
      <LearnPhaseOne
        word={currentLearnWord}
        progressText={currentLearnProgressText}
        options={record.options}
        triedOptions={record.triedOptions}
        onChooseOption={handleLearnChoice}
        onOpenDetail={() => openWordDetail(currentLearnWord.id, { type: "learn", stage: 1 })}
        onPrev={handlePrevInFlow}
        onStar={handleStarCurrentWord}
        onSkip={handleSkipInFlow}
        onHome={handleGoHomeFromFlow}
        disablePrev={state.learnSession.stage1Index === 0}
      />
    );
  };

  const renderLearn2 = () => {
    if (!currentLearnWord || !state.learnSession) return renderHome();
    const record = state.learnSession.phase2Map[currentLearnWord.id];
    return (
      <LearnPhaseTwo
        word={currentLearnWord}
        progressText={currentLearnProgressText}
        showHint={record.showHint}
        onShowHint={() => setState(prev => ({
          ...prev,
          learnSession: {
            ...prev.learnSession,
            phase2Map: {
              ...prev.learnSession.phase2Map,
              [currentLearnWord.id]: { ...prev.learnSession.phase2Map[currentLearnWord.id], showHint: true }
            }
          }
        }))}
        onKnow={handleLearnPhase2Know}
        onDontKnow={handleLearnPhase2DontKnow}
        onPrev={handlePrevInFlow}
        onStar={handleStarCurrentWord}
        onSkip={handleSkipInFlow}
        onHome={handleGoHomeFromFlow}
        disablePrev={state.learnSession.stage2Index === 0}
      />
    );
  };

  const renderLearn3 = () => {
    if (!currentLearnWord || !state.learnSession) return renderHome();
    const record = state.learnSession.phase3Map?.[currentLearnWord.id] || { inputValue: "", checkResult: "idle" };
    return (
      <LearnPhaseThree
        word={currentLearnWord}
        progressText={currentLearnProgressText}
        inputValue={record.inputValue}
        checkResult={record.checkResult}
        onChangeInput={val => setState(prev => ({
          ...prev,
          learnSession: {
            ...prev.learnSession,
            phase3Map: {
              ...prev.learnSession.phase3Map,
              [currentLearnWord.id]: { ...prev.learnSession.phase3Map[currentLearnWord.id], inputValue: val, checkResult: "idle" }
            }
          }
        }))}
        onCheck={handleLearnPhase3Check}
        onPrev={handlePrevInFlow}
        onStar={handleStarCurrentWord}
        onSkip={handleSkipInFlow}
        onHome={handleGoHomeFromFlow}
        disablePrev={state.learnSession.stage3Index === 0}
      />
    );
  };

  const renderReview1 = () => {
    if (!currentReviewWord || !state.reviewSession) return renderHome();
    return (
      <ReviewPhaseOne
        word={currentReviewWord}
        progressText={currentReviewProgressText}
        heartCount={state.user.energy || 0}
        onKnown={handleReviewKnown}
        onPrev={handlePrevInFlow}
        onStar={handleStarCurrentWord}
        onSkip={handleSkipInFlow}
        onHome={handleGoHomeFromFlow}
        disablePrev={state.reviewSession.stage1Index === 0}
      />
    );
  };

  const renderReview2 = () => (
    <div style={{ textAlign:"center",padding:"30px 16px" }}>  {/* 🔥 从 60px 20px 缩小到 30px 16px */}
      <ReviewPhaseTwoInput
        word={currentReviewWord}
        progressText={currentReviewProgressText}
        inputValue={state.reviewSession?.phase2Map?.[currentReviewWord?.id]?.inputValue || ""}
        checkResult={state.reviewSession?.phase2Map?.[currentReviewWord?.id]?.checkResult || "idle"}
        onChangeInput={(val) => setState(prev => ({
          ...prev,
          reviewSession: {
            ...prev.reviewSession,
            phase2Map: {
              ...prev.reviewSession.phase2Map,
              [currentReviewWord.id]: { ...(prev.reviewSession.phase2Map?.[currentReviewWord.id] || {}), inputValue: val, checkResult: "idle" }
            }
          }
        }))}
        onCheck={() => handleReviewPhase2Check()}
        onPrev={handlePrevInFlow}
        onStar={handleStarCurrentWord}
        onSkip={handleSkipInFlow}
        onHome={handleGoHomeFromFlow}
        disablePrev={state.reviewSession.stage2Index === 0}
      />
    </div>
  );

  const handleReviewPhase2Check = () => {
    if (!currentReviewWord || !state.reviewSession) return;
    const id = currentReviewWord.id;
    const input = state.reviewSession.phase2Map?.[id]?.inputValue || "";
    const candidates = [
      currentReviewWord.translation,
      ...(currentReviewWord.common_meanings || [])
    ].map(normalizeText);
    const ok = candidates.includes(normalizeText(input));

    // 更新本地UI状态
    setState(prev => {
      const s = { ...prev.reviewSession };
      s.phase2Map = { ...s.phase2Map, [id]: { ...(s.phase2Map?.[id] || {}), checkResult: ok ? "correct" : "wrong" } };
      return { ...prev, reviewSession: s };
    });

    // 记录到后端（回退到本地也已处理）
    (async () => {
      try {
        await learningApi.recordStudyResult(id, ok ? "correct" : "wrong", 25);
      } catch (err) {
        console.warn("记录复习阶段2到后端失败：", err);
      }
    })();

    // 前进到下一题
    setTimeout(() => {
      setState(prev => {
        const s = { ...prev.reviewSession };
        const next = s.stage2Index + 1;
        if (next >= s.words.length) {
          s.currentWordId = s.words[0]; s.stage2Index = 0;
          const failed = prev.reviewSession?.failedWords || [];
          if (failed && failed.length > 0) {
            const ids = failed.slice(0, prev.user.dailyPlanCount || 10);
            const phase1Map = {}; const phase2Map = {}; const phase3Map = {};
            ids.forEach(id => { phase1Map[id] = {}; phase2Map[id] = {}; phase3Map[id] = {}; });
            return {
              ...prev,
              learnSession: {
                stage: 1,
                words: ids,
                stage1Index: 0,
                stage2Index: 0,
                stage3Index: 0,
                currentWordId: ids[0],
                phase1Map, phase2Map, phase3Map, completed: false
              },
              ui: { ...prev.ui, screen: "learn1" }
            };
          }
          return { ...prev, reviewSession: s, ui: { ...prev.ui, screen: "reviewComplete" } };
        }
        s.stage2Index = next; s.currentWordId = s.words[next];
        return { ...prev, reviewSession: s };
      });
    }, 700);
  };

  const renderMyContent = () => (
    <MyContentPage
      state={state}
      starredWords={starredWords}
      learnedWords={learnedWords}
      words={words}
      message={state.ui.message}
      onGoHome={goHome}
      onOpenWordDetail={openWordDetail}
      onSpeakFrench={speakFrench}
      // 新增：词书切换功能
      books={ALL_BOOKS}
      currentBook={currentBook}
      onSwitchBook={handleSwitchBook}
    />
  );

  const renderStatistics = () => (
    <StatisticsPage
      state={state}
      onGoHome={goHome}
      showTempMessage={showTempMessage}
      openWordDetail={openWordDetail}
      speakFrench={speakFrench}
    />
  );

  const renderLearnComplete = () => (
    <CompleteCard
      emoji="✨"
      title="本组新词学习完成"
      desc={<>你已完成本次学习<br />每学会一词 +1 能量</>}
      buttonText="进入拼写选择"
      onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "spellingPrompt" } }))}
      style={{ ...buttonStyle, ...hoverButtonStyle }}
    >
      <div style={{ marginTop: "1rem" }}>
        <button onClick={()=>{goHome(); dropRandomShards("learn_complete");}} style={{
          width: "100%", border: `1px solid ${COLORS.line || COLORS.border}`, borderRadius: "14px",
          background: "#fff", padding: "0.9rem 1rem", fontWeight: 700, color: COLORS.text || COLORS.textPrimary, cursor: "pointer",
          ...buttonStyle, ...hoverButtonStyle
        }}>返回主页（掉落碎片）</button>
      </div>
    </CompleteCard>
  );

  const renderReviewComplete = () => (
    <CompleteCard
      emoji="🎓"
      title="本轮复习完成"
      desc={<>复习已完成<br />下次复习已自动安排</>}
      buttonText="进入拼写选择"
      onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, screen: "spellingPrompt" } }))}
      style={{ ...buttonStyle, ...hoverButtonStyle }}
    >
      <div style={{ marginTop: "1rem" }}>
        <button onClick={()=>{goHome(); dropRandomShards("review_complete");}} style={{
          width: "100%", border: `1px solid ${COLORS.line || COLORS.border}`, borderRadius: "14px",
          background: "#fff", padding: "0.9rem 1rem", fontWeight: 700, color: COLORS.text || COLORS.textPrimary, cursor: "pointer",
          ...buttonStyle, ...hoverButtonStyle
        }}>返回主页（掉落碎片）</button>
      </div>
    </CompleteCard>
  );

  const renderSpellingPrompt = () => {
    const source = state.learnSession && state.ui.screen !== "reviewComplete" ? "learn" : "review";
    const wordIds = source === "review" ? [] : state.learnSession?.words || [];
    return (
      <SpellingPromptPage
        onStart={() => startSpellingFromWords(wordIds, source)}
        onSkip={goHome}
        style={{ ...buttonStyle, ...hoverButtonStyle }}
      />
    );
  };

  const renderSpelling = () => (
    <SpellingPracticePage
      word={currentSpellingWord}
      progressText={`${(state.spellingSession?.index || 0) + 1}/${state.spellingSession?.words?.length || 0}`}
      inputValue={state.spellingSession?.inputValue || ""}
      checkState={state.spellingSession?.checkState || "idle"}
      wrongHint={state.spellingSession?.wrongHint || ""}
      showSpellingHint={state.spellingSession?.showSpellingHint || false}
      onShowHint={() => {
        setState(prev => ({ ...prev, spellingSession: { ...prev.spellingSession, showSpellingHint: true } }));
        setTimeout(() => {
          setState(prev => {
            if (!prev.spellingSession) return prev;
            return { ...prev, spellingSession: { ...prev.spellingSession, showSpellingHint: false } };
          });
        }, 1200);
      }}
      onChangeInput={val => setState(prev => ({
        ...prev,
        spellingSession: { ...prev.spellingSession, inputValue: val, checkState: "idle", wrongHint: "" }
      }))}
      onCheck={handleCheckSpelling}
      onPrev={handlePrevInFlow}
      onStar={handleStarCurrentWord}
      onSkip={handleSkipInFlow}
      onHome={handleGoHomeFromFlow}
      disablePrev={(state.spellingSession?.index || 0) === 0}
      style={{ ...buttonStyle, ...hoverButtonStyle }}
    />
  );

  const renderSpellingComplete = () => {
    const results = Object.values(state.spellingSession?.results || {}).map(item => ({
      ...item,
      word: getWordById(item.wordId, words)?.word || ""
    }));
    return (
      <CompleteCard
        emoji="👏"
        title="拼写练习完成"
        desc={<>Bon travail<br />本轮拼写已完成</>}
        buttonText="返回主页"
        onClick={() => { setState(prev => ({ ...prev, spellingSession: null })); goHome(); dropRandomShards("spelling_complete"); }}
        style={{ ...buttonStyle, ...hoverButtonStyle }}
      >
        <SpellingSummaryCard results={results} />
      </CompleteCard>
    );
  };

  const renderStarredWords = () => (
    <StarredWordsPage
      starredWords={starredWords}
      goHome={goHome}
      openWordDetail={openWordDetail}
      handleStarCurrentWord={handleStarCurrentWord}
      handleUnstarWord={handleUnstarWord}
      speakFrench={speakFrench}
      COLORS={COLORS}
      style={{ ...buttonStyle, ...hoverButtonStyle }}
    />
  );

  const MainApp = () => {
    if (!state.app.hasShownEntrance) {
      return (
        <EntranceAnimation
          onFinish={() => setState(prev => ({
            ...prev,
            app: { ...prev.app, hasShownEntrance: true },
            ui: { ...prev.ui, screen: "home" }
          }))}
        />
      );
    }

    switch (state.ui.screen) {
      case "home": return renderHome();
      case "detail": return renderDetail();
      case "starredWords": return renderStarredWords();
      case "learn1": return renderLearn1();
      case "learn2": return renderLearn2();
      case "learn3": return renderLearn3();
      case "review1": return renderReview1();
      case "review2": return renderReview2();
      case "myContent": return renderMyContent();
      case "statistics": return renderStatistics();
      case "learnComplete": return renderLearnComplete();
      case "reviewComplete": return renderReviewComplete();
      case "spellingPrompt": return renderSpellingPrompt();
      case "spelling": return renderSpelling();
      case "spellingComplete": return renderSpellingComplete();
      default: return renderHome();
    }
  };

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/" element={
          <PrivateRoute>
            <MainApp />
          </PrivateRoute>
        } />
        <Route path="/ebbinghaus-table" element={
          <PrivateRoute>
            <EbbinghausTable onGoHome={() => navigate("/")} />
          </PrivateRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* 🔥 已补全壁纸切换所需参数 */}
      {showMapModal && (
        <LandmarkCarousel
          unlockedLandmarks={state.user.unlockedLandmarks}
          userShards={state.user.shards}
          onUnlockLandmark={unlockLandmark}
          onEnterLandmark={enterLandmark}
          onClose={() => setShowMapModal(false)}
          onChangeSkin={changeSkin}
          onShowMessage={showTempMessage}
        />
      )}
    </>
  );
}